import { resources } from './modules';
import { HrGlobalSearch } from './employee-search';
import { PasswordRecoveryScreen } from './password-recovery';
import { EMAIL_ENABLED } from './features';
import { PublicPortal, isPublicPage } from './public-pages';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  activate,
  login,
  logout,
  restoreSession,
  sendMfaEmail,
  sessionStore,
  verifyMfa,
} from './api/session';
import { api, mutationHeaders, type Row, type Schema } from './api/client';
import { useAccess, useEmployee, useRows } from './api/data';
import { useInterfaceAppearance } from './appearance-runtime';
import { AccountMenu, ACCOUNT_MENU_ID } from './account-menu';
import { SettingsShell, isSettingsPath } from './settings-shell';
import {
  AdministrativeNavigation,
  adminNavigation,
  administrativeDestination,
} from './administrative/common';
import {
  WorkAreaContent,
  WorkAreaSwitcher,
  canOpenWorkAreaPath,
  useWorkArea,
  workAreaForPath,
  workAreaHome,
  type WorkArea,
} from './work-areas';
import {
  Select,
  Avatar,
  Button,
  Empty,
  ErrorState,
  Field,
  Icon,
  IconButton,
  Loading,
  Overlay,
  PersonCell,
  ToastContext,
} from './ui';

export const navigation = [
  { title: 'Обзор', path: '/', icon: 'Utilities', permission: '' },
  {
    title: 'Подбор персонала',
    path: '/recruitment/requisitions',
    section: 'recruitment',
    resource: 'requisitions',
    icon: 'Workflow',
    permission: 'requisitions.read',
  },
  { title: 'Сотрудники', path: '/people/employees', icon: 'People', permission: 'employees.read' },
  {
    title: 'Организация',
    path: '/organisation/departments',
    section: 'organisation',
    resource: 'departments',
    icon: 'Bank',
    permission: 'organization.read',
  },
  {
    title: 'HR-процессы',
    path: '/hr/onboarding',
    section: 'hr',
    resource: 'onboarding',
    icon: 'DocumentChecked',
    permission: 'onboarding.read',
  },
  {
    title: 'Эффективность',
    path: '/performance/role-profiles',
    section: 'performance',
    resource: 'role-profiles',
    icon: 'StarFilled',
    permission: 'performance_templates.read',
  },
  {
    title: 'Администрирование',
    path: '/admin/accounts',
    section: 'admin',
    resource: 'accounts',
    icon: 'Controls',
    permission: 'accounts.read',
  },
  {
    title: 'Рабочее пространство',
    path: '/admin/workspace',
    icon: 'Controls',
    permission: 'workspace.read',
  },
  { title: 'Открытые вакансии', path: '/careers', icon: 'People', permission: '' },
  { title: 'Мой профиль', path: '/my-profile', icon: 'NationalId', permission: 'employees.read' },
] as const;

export function useSession() {
  return useSyncExternalStore(sessionStore.subscribe, sessionStore.getSnapshot);
}

function AuthScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [methods, setMethods] = useState<('EMAIL' | 'TOTP')[] | null>(null);
  const [method, setMethod] = useState<'EMAIL' | 'TOTP'>('EMAIL');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const state = useSession();
  const activation =
    new URLSearchParams(location.hash.slice(1)).get('token') ||
    new URLSearchParams(location.hash.slice(1)).get('activation');
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      if (activation) {
        await activate(activation, password);
        history.replaceState(null, '', '/');
        window.location.assign('/');
      } else if (methods) await verifyMfa(method, code);
      else {
        const result = await login(email, password);
        if ('methods' in result) {
          setMethods(result.methods);
          setMethod(result.methods.includes('TOTP') ? 'TOTP' : 'EMAIL');
        }
      }
    } catch (value) {
      setError(value instanceof Error ? value : new Error('Не удалось войти'));
    } finally {
      setPending(false);
    }
  };
  return (
    <main className="auth-page">
      <div className="auth-brand">
        <img src="/assets/snaphire-icon.png" alt="" />
        <span>SnapHire</span>
      </div>
      <div className="auth-card">
        <h1>
          {activation ? 'Активация учётной записи' : methods ? 'Подтверждение входа' : 'Войти'}
        </h1>
        <p className="muted">
          {activation
            ? 'Задайте пароль для начала работы'
            : methods
              ? 'Введите код подтверждения'
              : 'Добро пожаловать в SnapHire'}
        </p>
        <form onSubmit={submit}>
          {!methods && !activation && (
            <Field
              label="Рабочая почта"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
          {!methods && (
            <Field
              label={activation ? 'Новый пароль' : 'Пароль'}
              type="password"
              autoComplete={activation ? 'new-password' : 'current-password'}
              required
              minLength={12}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
          {methods && (
            <>
              <Field label="Способ подтверждения">
                <Select
                  value={method}
                  onChange={(event) => setMethod(event.target.value as 'EMAIL' | 'TOTP')}
                >
                  {methods.map((value) => (
                    <option key={value} value={value}>
                      {value === 'TOTP' ? 'Приложение-аутентификатор' : 'Электронная почта'}
                    </option>
                  ))}
                </Select>
              </Field>
              <Field
                label="Код подтверждения"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                required
                value={code}
                onChange={(event) => setCode(event.target.value)}
              />
              {method === 'EMAIL' && (
                <Button
                  onClick={async () => {
                    try {
                      await sendMfaEmail();
                    } catch (value) {
                      setError(value as Error);
                    }
                  }}
                >
                  Отправить код
                </Button>
              )}
            </>
          )}
          {EMAIL_ENABLED && !methods && !activation && (
            <Link to="/forgot-password">Не помню пароль</Link>
          )}
          {(error || state.error) && <ErrorState error={error || new Error(state.error!)} />}
          <Button type="submit" className="primary full" disabled={pending}>
            {pending ? 'Подождите…' : methods ? 'Подтвердить' : activation ? 'Продолжить' : 'Войти'}
          </Button>
          {methods && (
            <Button
              onClick={() => {
                setMethods(null);
                setPassword('');
                setCode('');
              }}
            >
              Назад
            </Button>
          )}
        </form>
      </div>
    </main>
  );
}

function Notifications({ onClose }: { onClose: () => void }) {
  const notifications = useRows<Schema<'NotificationResponse'>>('/api/v1/notifications');
  const queryClient = useQueryClient();
  const [error, setError] = useState<Error | null>(null);
  return (
    <Overlay title="Уведомления" onClose={onClose}>
      <div className="drawer-toolbar">
        <Button
          onClick={async () => {
            const result = await api.POST('/api/v1/notifications/read-all', {
              params: { header: mutationHeaders() },
            });
            if (result.error) setError(new Error('Не удалось отметить уведомления прочитанными'));
            else await queryClient.invalidateQueries({ queryKey: ['/api/v1/notifications'] });
          }}
        >
          Прочитать все
        </Button>
      </div>
      {error && <ErrorState error={error} />}
      {notifications.isLoading ? (
        <Loading />
      ) : notifications.error ? (
        <ErrorState
          error={notifications.error}
          retry={() => {
            void notifications.refetch();
          }}
        />
      ) : !notifications.rows.length ? (
        <Empty icon="Bell" title="Новых уведомлений нет" subtitle="Здесь появятся уведомления" />
      ) : (
        notifications.rows.map((notification) => (
          <button
            type="button"
            className={`notification ${notification.readAt ? '' : 'unread'}`}
            key={notification.id}
            onClick={async () => {
              const result = await api.POST('/api/v1/notifications/{notificationId}/read', {
                params: { path: { notificationId: notification.id }, header: mutationHeaders() },
              });
              if (result.error) setError(new Error('Не удалось отметить уведомление прочитанным'));
              else await queryClient.invalidateQueries({ queryKey: ['/api/v1/notifications'] });
            }}
          >
            <Icon name="Bell" />
            <span>
              <strong>{notification.title}</strong>
              <small>{notification.body}</small>
              <time>{new Date(notification.createdAt).toLocaleString()}</time>
            </span>
          </button>
        ))
      )}
    </Overlay>
  );
}

export function AppRoot() {
  const session = useSession();
  const access = useAccess();
  const location = useRouterState({ select: (state) => state.location });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [overlay, setOverlay] = useState<'search' | 'notifications' | 'profile' | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const profileAnchor = useRef<HTMLButtonElement>(null);
  const lastApplicationPath = useRef('/');
  const close = useCallback(() => setOverlay(null), []);
  const publicPage = isPublicPage(location.pathname);
  const settingsPage = isSettingsPath(location.pathname);
  const currentAccess =
    access.data?.account.id === session.account?.id &&
    access.data?.account.workspaceId === session.account?.workspaceId
      ? access.data
      : undefined;
  const workArea = useWorkArea(
    publicPage ? null : session.account,
    currentAccess?.workAreas,
    queryClient,
    location.pathname,
  );
  const employee = useEmployee(
    !publicPage && !settingsPage && workArea.active === 'HR' && !workArea.changing
      ? session.account?.employeeId || undefined
      : undefined,
  );
  useInterfaceAppearance(publicPage ? null : session.account, access.data);
  const contentAllowed = canOpenWorkAreaPath(location.pathname, workArea.allowed, workArea.active);
  useEffect(() => {
    if (publicPage || !session.account || !currentAccess || workArea.changing) return;
    const requested = workAreaForPath(location.pathname);
    if (requested && requested !== workArea.active && workArea.allowed.includes(requested)) {
      void workArea.change(requested);
      return;
    }
    if (workArea.active && !contentAllowed)
      void navigate({ to: workAreaHome(workArea.active), replace: true });
  }, [
    publicPage,
    session.account,
    currentAccess,
    workArea.active,
    workArea.changing,
    contentAllowed,
    navigate,
    location.pathname,
  ]);
  useEffect(() => {
    setMobileOpen(false);
    setOverlay(null);
  }, [workArea.active]);
  const changeWorkArea = async (area: WorkArea) => {
    setMobileOpen(false);
    setOverlay(null);
    try {
      if (await workArea.change(area, () => navigate({ to: workAreaHome(area) })))
        lastApplicationPath.current = workAreaHome(area);
    } catch {
      setToast('Не удалось переключить пространство. Попробуйте ещё раз.');
    }
  };
  useEffect(() => {
    if (!settingsPage && !publicPage && session.account)
      lastApplicationPath.current = location.pathname;
  }, [settingsPage, publicPage, location.pathname, session.account]);
  useEffect(() => {
    if (!publicPage) void restoreSession();
  }, [publicPage]);
  useEffect(() => {
    setMobileOpen(false);
    setOverlay(null);
  }, [location.pathname]);
  useEffect(() => {
    if (toast) {
      const timeout = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timeout);
    }
  }, [toast]);
  if (publicPage) return <PublicPortal />;
  if (!session.ready)
    return (
      <div className="startup">
        <Loading />
      </div>
    );
  if (['/forgot-password', '/reset-password'].includes(location.pathname))
    return <PasswordRecoveryScreen />;
  if (!session.account) return <AuthScreen />;
  if (settingsPage)
    return (
      <ToastContext.Provider value={setToast}>
        <SettingsShell
          path={location.pathname}
          onClose={() =>
            void navigate({
              to: (workArea.active &&
              !canOpenWorkAreaPath(lastApplicationPath.current, workArea.allowed, workArea.active)
                ? workAreaHome(workArea.active)
                : lastApplicationPath.current) as '/',
            })
          }
        >
          <WorkAreaContent
            ready={Boolean(currentAccess)}
            changing={workArea.changing}
            allowed={workArea.allowed}
            active={workArea.active}
            path={location.pathname}
            error={access.error}
            retry={() => void access.refetch()}
          >
            <Outlet />
          </WorkAreaContent>
        </SettingsShell>
        {toast && (
          <div role="status" className="toast">
            <Icon name="InfoOutline" />
            {toast}
          </div>
        )}
      </ToastContext.Provider>
    );
  const pathname = location.pathname;
  const item =
    navigation.find(
      (item) => !('section' in item) && item.path !== '/' && pathname.startsWith(item.path),
    ) ||
    navigation.find((item) =>
      item.path === '/'
        ? pathname === '/'
        : pathname.startsWith('section' in item ? `/${item.section}/` : item.path),
    );
  const title =
    pathname === '/administrative' || pathname.startsWith('/administrative/')
      ? adminNavigation.find(
          (nav) => nav.path && pathname.startsWith(`/administrative/${nav.path}`),
        )?.title ||
        (pathname.startsWith('/administrative/invites')
          ? 'Приглашения'
          : pathname.startsWith('/administrative/sheets')
            ? 'Ресурсы'
            : 'Обзор')
      : pathname.startsWith('/employees/profile/')
        ? 'Профиль'
        : pathname === '/my-profile'
          ? 'Мой профиль'
          : pathname.startsWith('/operations')
            ? 'Импорт и экспорт'
            : item?.title ||
              (pathname.startsWith('/recruitment/')
                ? 'Подбор персонала'
                : pathname.startsWith('/hr/')
                  ? 'HR-процессы'
                  : 'Сотрудники');
  const parts = pathname.split('/');
  const showBack =
    pathname.startsWith('/account/') ||
    (parts.length >= 4 &&
      ![
        '/performance/goals/general',
        '/performance/reviews/general',
        '/organisation/teams/departments',
      ].includes(pathname));
  const goBack = () => {
    if (pathname.startsWith('/administrative/')) {
      const target =
        parts.length > 4
          ? parts.slice(2, -1).join('/')
          : parts[2] === 'sheets'
            ? 'sections'
            : parts[2]!;
      void navigate(administrativeDestination(target));
    } else if (pathname.startsWith('/operations/result/'))
      void navigate({
        to: '/operations/$mode',
        params: { mode: access.can('exports.run') ? 'export' : 'import' },
      });
    else if (pathname === '/admin/workspace/edit') void navigate({ to: '/admin/workspace' });
    else if (pathname.includes('/submission/'))
      void navigate({
        to: '/$section/$resource/$entityId',
        params: { section: 'performance', resource: 'reviews', entityId: parts[3]! },
      });
    else if (pathname.startsWith('/account/'))
      void navigate({ to: pathname === '/account/settings' ? '/' : '/account/settings' });
    else if (pathname.startsWith('/employees/profile/')) {
      if (parts.length > 4)
        void navigate({
          to: '/employees/profile/$employeeId',
          params: { employeeId: parts[3]! },
          search: { tab: (location.search as { kind?: string }).kind },
        });
      else void navigate({ to: '/people/employees' });
    } else if (parts.length === 4 && (location.search as { parentId?: string }).parentId) {
      const resource = resources.find((item) => item.section === parts[1] && item.key === parts[2]);
      void navigate({
        to: '/$section/$resource/$entityId',
        params: {
          section: parts[1]!,
          resource: resource?.parentScoped || parts[2]!,
          entityId: (location.search as { parentId: string }).parentId,
        },
      });
    } else if (parts.length > 4)
      void navigate({
        to: '/$section/$resource/$entityId',
        params: { section: parts[1]!, resource: parts[2]!, entityId: parts[3]! },
        search: { parentId: (location.search as { parentId?: string }).parentId },
      });
    else
      void navigate({
        to: '/$section/$resource',
        params: { section: parts[1]!, resource: parts[2]! },
      });
  };
  const displayName = employee.data?.workName || session.account.login;
  const renderLink = (navItem: (typeof navigation)[number]) => {
    const allowed =
      'section' in navItem
        ? resources.filter(
            (resource) =>
              resource.section === navItem.section &&
              !resource.hidden &&
              (resource.readPermissions || [resource.readPermission]).some((permission) =>
                access.can(permission),
              ),
          )
        : [];
    if (
      'section' in navItem ? !allowed.length : navItem.permission && !access.can(navItem.permission)
    )
      return null;
    if (navItem.path === '/my-profile' && !session.account?.employeeId) return null;
    const content = (
      <>
        <Icon name={navItem.icon} />
        <span>{navItem.title}</span>
      </>
    );
    const props = {
      className: `nav-item ${item?.path === navItem.path ? 'active' : ''}`,
      title: navItem.title,
    };
    return 'section' in navItem ? (
      <Link
        key={navItem.path}
        to="/$section/$resource"
        params={{
          section: navItem.section,
          resource: allowed.some((resource) => resource.key === navItem.resource)
            ? navItem.resource
            : allowed[0]!.key,
        }}
        {...props}
      >
        {content}
      </Link>
    ) : (
      <Link
        key={navItem.path}
        to={navItem.path}
        activeOptions={{ exact: navItem.path === '/' }}
        {...props}
      >
        {content}
      </Link>
    );
  };
  return (
    <ToastContext.Provider value={setToast}>
      <div className={`app ${collapsed ? 'collapsed' : ''}`}>
        <aside className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
          <Link to={workArea.active ? workAreaHome(workArea.active) : '/'} className="brand">
            <img src="/assets/snaphire-icon.png" alt="" />
            <span>SnapHire</span>
          </Link>
          <nav aria-label="Основная навигация">
            {workArea.active === 'HR' ? (
              navigation.map(renderLink)
            ) : workArea.active === 'ADMINISTRATIVE' ? (
              <AdministrativeNavigation path={pathname} />
            ) : null}
            {workArea.active === 'HR' &&
              (access.can('candidates.import') || access.can('exports.run')) && (
                <Link
                  to="/operations/$mode"
                  params={{ mode: access.can('candidates.import') ? 'import' : 'export' }}
                  className={`nav-item ${pathname.startsWith('/operations') ? 'active' : ''}`}
                >
                  <Icon name="ChevronDown" />
                  <span>Импорт и экспорт</span>
                </Link>
              )}
          </nav>
          <button
            type="button"
            className="collapse-menu"
            aria-label={collapsed ? 'Развернуть меню' : 'Свернуть меню'}
            onClick={() => setCollapsed(!collapsed)}
          >
            <Icon name={collapsed ? 'SidePanelLeftClosed' : 'SidePanelLeftOpen'} />
          </button>
        </aside>
        {mobileOpen && (
          <button
            type="button"
            className="mobile-backdrop"
            aria-label="Закрыть меню"
            onClick={() => setMobileOpen(false)}
          />
        )}
        <header className={`topbar ${showBack ? 'has-back' : ''}`}>
          <div className="mobile-brand">
            <IconButton icon="Menu" label="Открыть меню" onClick={() => setMobileOpen(true)} />
            <Link to={workArea.active ? workAreaHome(workArea.active) : '/'}>
              <img src="/assets/snaphire-icon.png" alt="SnapHire" />
            </Link>
          </div>
          {showBack ? (
            <IconButton icon="BackButtonArrow" label="Назад" onClick={goBack} />
          ) : (
            <h1>{title}</h1>
          )}
          <div className="global-actions">
            <WorkAreaSwitcher
              allowed={workArea.allowed}
              active={workArea.active}
              disabled={workArea.changing}
              onChange={(area) => void changeWorkArea(area)}
            />
            {workArea.active === 'HR' && (
              <IconButton
                icon="Search"
                label="Открыть поиск"
                onClick={() => setOverlay('search')}
              />
            )}
            <IconButton
              icon="Bell"
              label="Открыть уведомления"
              onClick={() => setOverlay('notifications')}
            />
            <button
              type="button"
              ref={profileAnchor}
              className="profile-button"
              aria-label="Открыть меню учётной записи"
              aria-haspopup="menu"
              aria-expanded={overlay === 'profile'}
              aria-controls={overlay === 'profile' ? ACCOUNT_MENU_ID : undefined}
              onClick={() => setOverlay(overlay === 'profile' ? null : 'profile')}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  setOverlay('profile');
                }
              }}
            >
              <Avatar label={displayName} />
            </button>
          </div>
        </header>
        <main key={pathname} className={`page-main ${pathname === '/' ? 'home-main' : ''}`}>
          <WorkAreaContent
            ready={Boolean(currentAccess)}
            changing={workArea.changing}
            allowed={workArea.allowed}
            active={workArea.active}
            path={pathname}
            error={access.error}
            retry={() => void access.refetch()}
          >
            <Outlet />
          </WorkAreaContent>
        </main>
      </div>
      {overlay === 'search' && workArea.active === 'HR' && !workArea.changing && (
        <HrGlobalSearch onClose={close} />
      )}
      {overlay === 'notifications' && workArea.active && !workArea.changing && (
        <Notifications onClose={close} />
      )}
      {overlay === 'profile' && (
        <AccountMenu
          displayName={displayName}
          email={session.account.login}
          hasProfile={workArea.active === 'HR' && Boolean(session.account.employeeId)}
          anchorRef={profileAnchor}
          onClose={close}
          onLogout={() => {
            void logout()
              .then(() => {
                queryClient.clear();
                close();
              })
              .catch((error: unknown) =>
                setToast(error instanceof Error ? error.message : 'Не удалось выйти'),
              );
          }}
        />
      )}
      {toast && (
        <div role="status" className="toast">
          <Icon name="InfoOutline" />
          {toast}
        </div>
      )}
    </ToastContext.Provider>
  );
}

export function Panel({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="panel">
      <header>
        <h2>{title}</h2>
        {action}
      </header>
      {children}
    </section>
  );
}
export function RelatedPerson({ people, id }: { people: Row[]; id: string | null | undefined }) {
  return <PersonCell person={people.find((person) => person.id === id)} />;
}
